'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Link from 'next/link';

import {
  AlertCircle,
  AlertTriangle,
  Construction,
  Eraser,
  FileText,
  Loader2,
  Pill,
  Plus,
  Search,
  SearchX,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { formatDrugForm } from '../lib/drug-form';
import type { ChplQuote, ViewChplOptions } from '../types';

// Analytics tracking - fire and forget, never block UI
type SearchEvent = {
  eventType: 'search' | 'select';
  query?: string;
  drugId?: string;
  drugName?: string;
};

function trackSearchEvents(events: SearchEvent[]) {
  if (events.length === 0) return;

  // Fire and forget - don't await, don't block
  // Path avoids "analytics" to prevent ad-blocker interference
  fetch('/api/ev/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
  }).catch(() => {
    // Silently ignore errors - analytics should never break the app
  });
}

// Public Drug type (used in props)
export interface Drug {
  id: string;
  productName: string;
  commonName: string;
  strength: string;
  form: string;
  chplId?: string;
  /** Whether a ChPL PDF is available for this drug */
  hasChplPdf?: boolean;
  /** Whether a patient leaflet (ulotka) PDF is available for this drug */
  hasUlotkaPdf?: boolean;
  /** Whether ChPL extraction is available for this drug. If false, drug will be skipped from analysis. */
  hasChplExtraction?: boolean;
}

// Internal type with search field
interface InternalDrug extends Drug {
  searchText: string;
}

interface DrugSearchProps {
  selectedDrugs: Drug[];
  onDrugsChange: (drugs: Drug[]) => void;
  placeholder?: string;
  footer?: ReactNode;
  onViewChpl?: (quote: ChplQuote, options?: ViewChplOptions) => void;
  /** Callback when user wants to view a patient leaflet (ulotka) */
  onViewUlotka?: (quote: ChplQuote) => void;
  /** When true, show drugs without ChPL extraction (admin only). Default: false */
  showUnavailableDrugs?: boolean;
  /** Additional class names for the search input element */
  inputClassName?: string;
  /** Maximum drugs that can be selected (tier-dependent). Falls back to MAX_DRUGS_FALLBACK. */
  maxDrugs?: number;
  /** Content rendered inside the input container on the right (desktop only) */
  inputEndAddon?: ReactNode;
  /** Whether the limit-reached upsell links to /pricing (otherwise /contact) */
  showPricing?: boolean;
  /** Account is on a dedicated (custom) plan — the upsell always points to contact */
  hasCustomPlan?: boolean;
}

// Highlight colors for different search terms (outside component to avoid re-creation)
const HIGHLIGHT_COLORS = [
  'bg-yellow-200 dark:bg-yellow-700/70', // Term 1 - yellow
  'bg-cyan-200 dark:bg-cyan-700/70', // Term 2 - cyan
  'bg-pink-200 dark:bg-pink-700/70', // Term 3 - pink
  'bg-lime-200 dark:bg-lime-700/70', // Term 4 - lime
  'bg-orange-200 dark:bg-orange-700/70', // Term 5 - orange
];

// Style for characters that are in result but NOT in search query (missing from query)
// Red wavy/dashed underline to indicate "extra" character
const MISSING_CHAR_STYLE =
  'underline decoration-wavy decoration-red-400 dark:decoration-red-500';

export function DrugSearch({
  selectedDrugs,
  onDrugsChange,
  placeholder,
  footer,
  onViewChpl,
  onViewUlotka,
  showUnavailableDrugs = false,
  inputClassName,
  maxDrugs = MAX_DRUGS_FALLBACK,
  inputEndAddon,
  showPricing = false,
  hasCustomPlan = false,
}: DrugSearchProps) {
  const { t, i18n } = useTranslation('interactions');
  const language = i18n.language;
  const [drugs, setDrugs] = useState<InternalDrug[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [visibleCount, setVisibleCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const hasDrugs = selectedDrugs.length > 0;

  // Check if max drugs reached
  const isMaxDrugsReached = selectedDrugs.length >= maxDrugs;
  // Dedicated plans are negotiated individually, so never send them to /pricing
  const upsellToPricing = showPricing && !hasCustomPlan;

  const RESULTS_PER_PAGE = 20;

  // Load drugs data and add searchText field
  useEffect(() => {
    fetch('/data/chpl_metadata/drugs.json', { cache: 'no-store' })
      .then((res) => res.json())
      .then((data: Drug[]) => {
        // Add combined searchText for better multi-term matching
        // Include strength and form for extended search
        const drugsWithSearchText: InternalDrug[] = data.map((drug) => ({
          ...drug,
          searchText:
            `${drug.productName ?? ''} ${drug.commonName ?? ''} ${drug.strength ?? ''} ${drug.form ?? ''}`.toLowerCase(),
        }));
        setDrugs(drugsWithSearchText);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load drugs data:', error);
        setIsLoading(false);
      });
  }, []);

  // Auto-focus search input on mount when no drugs are selected
  useEffect(() => {
    if (selectedDrugs.length > 0) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search results with multi-term support (order-independent) and smart ranking
  // Uses Levenshtein distance for true fuzzy matching
  const allSearchResults = useMemo(() => {
    if (drugs.length === 0 || !query.trim()) return [];

    const searchQuery = query.trim().toLowerCase();

    // Filter out already selected drugs
    const selectedIds = new Set(selectedDrugs.map((d) => d.id));

    const isAvailable = (drug: InternalDrug) =>
      showUnavailableDrugs || drug.hasChplExtraction !== false;

    // ID search: if query is purely numeric, match by drug ID (no highlighting)
    if (/^\d+$/.test(searchQuery)) {
      return drugs
        .filter(
          (drug) =>
            !selectedIds.has(drug.id) &&
            isAvailable(drug) &&
            drug.id.startsWith(searchQuery),
        )
        .map((drug) => ({ item: drug, rankScore: 0, termMatches: [] }));
    }

    const terms = searchQuery
      .split(/\s+/)
      .filter((t) => t.length >= 2 || /^\d+$/.test(t));

    if (terms.length === 0) return [];

    // For each drug, check if ALL terms match (fuzzy) and calculate score
    // This is order-independent: "bil cla" matches same as "cla bil"
    const scoredResults: Array<{
      item: InternalDrug;
      rankScore: number;
      termMatches: Array<{
        term: string;
        type: 'exact' | 'fuzzy';
        position: number;
        field: 'product' | 'common' | 'strength' | 'form';
        distance: number;
      }>;
    }> = [];

    for (const drug of drugs) {
      if (selectedIds.has(drug.id)) continue;
      if (!isAvailable(drug)) continue;

      const productNameLower = (drug.productName ?? '').toLowerCase();
      const commonNameLower = (drug.commonName ?? '').toLowerCase();
      const strengthLower = (drug.strength ?? '').toLowerCase();
      const formLower = (drug.form ?? '').toLowerCase();

      // Check how each term matches
      // field: 'product' | 'common' | 'strength' | 'form' - indicates which field matched
      const termMatches: Array<{
        term: string;
        type: 'exact' | 'fuzzy';
        position: number;
        field: 'product' | 'common' | 'strength' | 'form';
        distance: number;
      } | null> = terms.map((term) => {
        // Check exact match in product name (highest priority)
        const exactInProduct = productNameLower.indexOf(term);
        if (exactInProduct !== -1) {
          return {
            term,
            type: 'exact' as const,
            position: exactInProduct,
            field: 'product' as const,
            distance: 0,
          };
        }

        // Check exact match in common name (high priority)
        const exactInCommon = commonNameLower.indexOf(term);
        if (exactInCommon !== -1) {
          return {
            term,
            type: 'exact' as const,
            position: exactInCommon,
            field: 'common' as const,
            distance: 0,
          };
        }

        // Check fuzzy match using Levenshtein distance in product/common names
        // Find best matching word in product name
        const productMatch = findBestWordMatch(productNameLower, term);
        const commonMatch = findBestWordMatch(commonNameLower, term);

        // Pick the better match from product/common names
        let bestMatch = productMatch;
        let matchField: 'product' | 'common' | 'strength' | 'form' = 'product';
        if (
          commonMatch &&
          (!productMatch || commonMatch.distance < productMatch.distance)
        ) {
          bestMatch = commonMatch;
          matchField = 'common';
        }

        // Accept fuzzy match based on score and relative distance
        // Require prefix match OR low relative distance
        if (bestMatch) {
          const wordPart = bestMatch.matchedWord
            .slice(0, term.length + 3)
            .toLowerCase();
          const relativeDistance =
            bestMatch.distance / Math.max(wordPart.length, term.length);
          const hasGoodPrefix = wordPart.startsWith(term.slice(0, 2));

          // Accept if: good prefix with reasonable distance, OR very low relative distance
          const isAcceptable =
            (hasGoodPrefix && relativeDistance <= 0.5) ||
            relativeDistance <= 0.35;

          if (isAcceptable) {
            return {
              term,
              type: 'fuzzy' as const,
              position: bestMatch.position,
              field: matchField,
              distance: bestMatch.distance,
            };
          }
        }

        // If no match in product/common names, check strength and form (lower priority)
        // Check exact match in strength
        const exactInStrength = strengthLower.indexOf(term);
        if (exactInStrength !== -1) {
          return {
            term,
            type: 'exact' as const,
            position: exactInStrength,
            field: 'strength' as const,
            distance: 0,
          };
        }

        // Check exact match in form
        const exactInForm = formLower.indexOf(term);
        if (exactInForm !== -1) {
          return {
            term,
            type: 'exact' as const,
            position: exactInForm,
            field: 'form' as const,
            distance: 0,
          };
        }

        // Check fuzzy match in strength and form
        const strengthMatch = findBestWordMatch(strengthLower, term);
        const formMatch = findBestWordMatch(formLower, term);

        // Pick the better match from strength/form
        let secondaryMatch = strengthMatch;
        let secondaryField: 'strength' | 'form' = 'strength';
        if (
          formMatch &&
          (!strengthMatch || formMatch.distance < strengthMatch.distance)
        ) {
          secondaryMatch = formMatch;
          secondaryField = 'form';
        }

        if (secondaryMatch) {
          const wordPart = secondaryMatch.matchedWord
            .slice(0, term.length + 3)
            .toLowerCase();
          const relativeDistance =
            secondaryMatch.distance / Math.max(wordPart.length, term.length);
          const hasGoodPrefix = wordPart.startsWith(term.slice(0, 2));

          const isAcceptable =
            (hasGoodPrefix && relativeDistance <= 0.5) ||
            relativeDistance <= 0.35;

          if (isAcceptable) {
            return {
              term,
              type: 'fuzzy' as const,
              position: secondaryMatch.position,
              field: secondaryField,
              distance: secondaryMatch.distance,
            };
          }
        }

        return null;
      });

      // All terms must match
      if (termMatches.some((m) => m === null)) continue;

      const validMatches = termMatches.filter(Boolean) as NonNullable<
        (typeof termMatches)[0]
      >[];

      // Calculate ranking score
      let score = 0;
      validMatches.forEach((match) => {
        // Base score depends on match type
        if (match.type === 'exact') {
          score += 50;
        } else {
          // Fuzzy matches: penalize by distance
          score += Math.max(0, 40 - match.distance * 15);
        }

        // Get the text for position checking based on field
        const fieldTextMap = {
          product: productNameLower,
          common: commonNameLower,
          strength: strengthLower,
          form: formLower,
        };
        const text = fieldTextMap[match.field];

        // Position bonus: earlier = better
        if (match.position === 0) {
          score += 100; // Starts at beginning
        } else {
          // Check if at word boundary
          const charBefore = text[match.position - 1];
          if (charBefore === ' ' || charBefore === '-' || charBefore === '(') {
            score += 60 - match.position * 0.5;
          } else {
            score += 20 - match.position * 0.2;
          }
        }

        // Field priority bonus (higher = better)
        // productName > commonName > strength/form
        switch (match.field) {
          case 'product':
            score += 30; // Highest priority
            break;
          case 'common':
            score += 20; // High priority
            break;
          case 'strength':
          case 'form':
            score += 5; // Lower priority - these are secondary search fields
            break;
        }
      });

      // Prefer shorter product names (simpler = better)
      // "Clatra" should rank higher than "Clatra Allergy"
      const lengthPenalty = productNameLower.length * 0.5;
      score -= lengthPenalty;

      scoredResults.push({
        item: drug,
        rankScore: score,
        termMatches: validMatches,
      });
    }

    // Sort by our custom score (higher = better)
    scoredResults.sort((a, b) => b.rankScore - a.rankScore);

    return scoredResults;
  }, [query, selectedDrugs, drugs, showUnavailableDrugs]);

  // Visible results (for infinite scroll)
  const visibleResults = useMemo(() => {
    return allSearchResults.slice(0, visibleCount);
  }, [allSearchResults, visibleCount]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!listRef.current || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isNearBottom && visibleCount < allSearchResults.length) {
      setIsLoadingMore(true);
      // Small delay to show loading animation
      setTimeout(() => {
        setVisibleCount((prev) => prev + RESULTS_PER_PAGE);
        setIsLoadingMore(false);
      }, 300);
    }
  }, [isLoadingMore, visibleCount, allSearchResults.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && visibleResults.length > 0) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, visibleResults.length]);

  // Close dropdown when tapping outside the search container
  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  const handleSelect = useCallback(
    (internalDrug: InternalDrug) => {
      // Don't allow adding more than maxDrugs
      if (selectedDrugs.length >= maxDrugs) {
        return;
      }

      // Convert InternalDrug to Drug (remove searchText)
      const drug: Drug = {
        id: internalDrug.id,
        productName: internalDrug.productName,
        commonName: internalDrug.commonName,
        strength: internalDrug.strength,
        form: internalDrug.form,
        chplId: internalDrug.chplId,
        hasChplPdf: internalDrug.hasChplPdf,
        hasUlotkaPdf: internalDrug.hasUlotkaPdf,
        hasChplExtraction: internalDrug.hasChplExtraction,
      };
      onDrugsChange([...selectedDrugs, drug]);
      setQuery('');
      setIsOpen(false);
      setHighlightedIndex(-1);
      // Use preventScroll to avoid iOS auto-scrolling the input into view,
      // which would push the "add second drug" hint off-screen
      inputRef.current?.focus({ preventScroll: true });

      // Analytics: track search query + drug selection together
      // Only track the final query that led to this selection
      const trimmedQuery = query.trim();
      const events: SearchEvent[] = [];

      // Track the search query if it's meaningful (2+ chars)
      if (trimmedQuery.length >= 2) {
        events.push({ eventType: 'search', query: trimmedQuery });
      }

      // Track the drug selection
      events.push({
        eventType: 'select',
        drugId: drug.id,
        drugName: drug.productName,
      });

      trackSearchEvents(events);
    },
    [selectedDrugs, onDrugsChange, query, maxDrugs],
  );

  const handleRemove = useCallback(
    (drugId: string) => {
      onDrugsChange(selectedDrugs.filter((d) => d.id !== drugId));
    },
    [selectedDrugs, onDrugsChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' && query.trim()) {
          setIsOpen(true);
          setHighlightedIndex(visibleResults.length > 0 ? 0 : -1);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (visibleResults.length === 0) return -1;
            if (prev < 0) return 0;
            return prev < visibleResults.length - 1 ? prev + 1 : prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (visibleResults.length === 0) return -1;
            if (prev < 0) return 0;
            return prev > 0 ? prev - 1 : 0;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && visibleResults[highlightedIndex]) {
            handleSelect(visibleResults[highlightedIndex].item);
          } else {
            // Dismiss keyboard on mobile while keeping dropdown open
            inputRef.current?.blur();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [isOpen, query, visibleResults, highlightedIndex, handleSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      setIsOpen(value.trim().length > 0);
      setHighlightedIndex(0);
      setVisibleCount(RESULTS_PER_PAGE); // Reset pagination on new search
    },
    [],
  );

  // Highlight matching text with different colors per term and grey for missing chars
  const highlightMatch = useCallback((text: string, searchQuery: string) => {
    if (!searchQuery.trim()) return escapeHtml(text);

    const terms = searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length >= 2 || /^\d+$/.test(t));
    const textLower = text.toLowerCase();

    // Find matches for each term
    const highlights: Array<{
      start: number;
      end: number;
      termIndex: number;
      term: string;
      isExact: boolean;
    }> = [];

    terms.forEach((term, termIndex) => {
      // First try exact match
      let idx = 0;
      let foundExact = false;
      while ((idx = textLower.indexOf(term, idx)) !== -1) {
        highlights.push({
          start: idx,
          end: idx + term.length,
          termIndex,
          term,
          isExact: true,
        });
        foundExact = true;
        idx += 1;
      }

      // If no exact match found, try fuzzy match
      if (!foundExact) {
        const fuzzyMatch = findFuzzyMatchWithDetails(textLower, term);
        if (fuzzyMatch) {
          highlights.push({
            start: fuzzyMatch.start,
            end: fuzzyMatch.end,
            termIndex,
            term,
            isExact: false,
          });
        }
      }
    });

    if (highlights.length === 0) return escapeHtml(text);

    // Sort by position
    highlights.sort((a, b) => a.start - b.start);

    // Handle overlapping - keep first match
    const finalHighlights: typeof highlights = [];
    for (const h of highlights) {
      let overlaps = false;
      for (const existing of finalHighlights) {
        if (h.start < existing.end && h.end > existing.start) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        finalHighlights.push(h);
      }
    }

    // Sort again after filtering
    finalHighlights.sort((a, b) => a.start - b.start);

    // Build highlighted string
    let result = '';
    let lastEnd = 0;

    finalHighlights.forEach(({ start, end, termIndex, term, isExact }) => {
      result += escapeHtml(text.slice(lastEnd, start));

      const colorClass = HIGHLIGHT_COLORS[termIndex % HIGHLIGHT_COLORS.length];
      const matchedText = text.slice(start, end);

      if (isExact) {
        // Exact match - simple highlight with term color
        result += `<mark class="${colorClass}">${escapeHtml(matchedText)}</mark>`;
      } else {
        // Fuzzy match - highlight matching chars in color, extra chars in grey
        result += highlightFuzzyMatch(matchedText, term, colorClass!);
      }

      lastEnd = end;
    });

    result += escapeHtml(text.slice(lastEnd));
    return result;
  }, []);

  return (
    <div className="w-full space-y-4">
      {/* Selected Drugs — rendered FIRST so search input follows below */}
      {selectedDrugs.length > 0 && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                onDrugsChange([]);
              }}
              className="inline-flex items-center gap-1 text-xs text-zinc-400 underline decoration-zinc-300 underline-offset-2 transition-colors hover:text-zinc-600 hover:decoration-zinc-400 dark:text-zinc-500 dark:decoration-zinc-600 dark:hover:text-zinc-300 dark:hover:decoration-zinc-400"
            >
              <Eraser className="h-3 w-3" />
              {t('search.clearList')}
            </button>
          </div>
          <div className="space-y-2">
            {selectedDrugs.map((drug) => {
              const isMissingChpl = drug.hasChplExtraction === false;

              // Render different layout for missing ChPL drugs (3A-1 design)
              if (isMissingChpl) {
                return (
                  <div
                    key={drug.id}
                    className="relative overflow-hidden rounded-lg border-2 border-zinc-400 shadow-[2px_2px_0_0_#a1a1aa] dark:border-zinc-500 dark:shadow-[2px_2px_0_0_#71717a]"
                    style={{
                      background: `repeating-linear-gradient(
                        -45deg,
                        #f4f4f5,
                        #f4f4f5 12px,
                        #fef9c3 12px,
                        #fef9c3 24px
                      )`,
                    }}
                  >
                    <div className="bg-white/90 p-3 dark:bg-zinc-900/90">
                      <div className="flex @[640px]:gap-3">
                        <Construction className="mt-1 hidden h-4 w-4 shrink-0 text-amber-500 @[640px]:block dark:text-amber-400" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-zinc-500 dark:text-zinc-400">
                              {drug.productName}
                            </span>
                            {drug.commonName && (
                              <span className="text-sm text-zinc-400 @[640px]:text-base dark:text-zinc-500">
                                ({drug.commonName})
                              </span>
                            )}
                            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">
                              {t('search.comingSoon')}
                            </span>
                          </div>
                          {((drug.strength && drug.strength !== '-') ||
                            drug.form) && (
                            <div className="mt-2 space-y-1 text-sm @[640px]:flex @[640px]:flex-wrap @[640px]:items-baseline @[640px]:space-y-0 @[640px]:gap-x-4 @[640px]:gap-y-1">
                              {drug.strength && drug.strength !== '-' && (
                                <div className="flex items-baseline gap-1.5">
                                  <span className="hidden text-xs font-medium tracking-wide text-zinc-300 uppercase @[640px]:inline dark:text-zinc-600">
                                    {t('search.strength')}
                                  </span>
                                  <span className="text-zinc-400 dark:text-zinc-500">
                                    {drug.strength}
                                  </span>
                                </div>
                              )}
                              {drug.form && (
                                <div className="flex items-baseline gap-1.5">
                                  <span className="hidden text-xs font-medium tracking-wide text-zinc-300 uppercase @[640px]:inline dark:text-zinc-600">
                                    {t('search.form')}
                                  </span>
                                  <span className="text-zinc-400 dark:text-zinc-500">
                                    {formatDrugForm(language, drug.form)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-col items-end">
                          <button
                            type="button"
                            onClick={() => handleRemove(drug.id)}
                            className="rounded border-2 border-zinc-400 bg-white p-0.5 text-zinc-400 shadow-[2px_2px_0_0_#a1a1aa] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-red-100 hover:text-red-600 hover:shadow-[1px_1px_0_0_#a1a1aa] dark:border-zinc-500 dark:bg-zinc-800 dark:text-zinc-500 dark:shadow-[2px_2px_0_0_#71717a] dark:hover:bg-red-900/50 dark:hover:text-red-400 dark:hover:shadow-[1px_1px_0_0_#71717a]"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Warning message + ChPL link on same line */}
                      <div className="mt-3 flex items-center justify-between gap-3 @[640px]:ml-7">
                        <div className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-600 dark:bg-amber-900/30">
                          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                          <span className="text-xs leading-tight font-medium text-amber-800 dark:text-amber-200">
                            {t('search.workingOnDrug')}
                          </span>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1 @[640px]:flex-row @[640px]:items-center @[640px]:gap-1.5">
                          {drug.hasChplPdf && onViewChpl && (
                            <button
                              type="button"
                              onClick={() =>
                                onViewChpl(
                                  {
                                    drugId: drug.id,
                                    chplId: drug.chplId,
                                    drugName: drug.productName,
                                    activeSubstance: drug.commonName,
                                    strength: drug.strength,
                                    form: drug.form,
                                    texts: [],
                                  },
                                  {
                                    hasChplPdf: drug.hasChplPdf,
                                    hasUlotka: drug.hasUlotkaPdf,
                                  },
                                )
                              }
                              className="inline-flex items-center gap-1 rounded bg-zinc-200/70 px-2 py-0.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-300 hover:text-zinc-800 dark:bg-zinc-700/70 dark:text-zinc-400 dark:hover:bg-zinc-600 dark:hover:text-zinc-300"
                            >
                              <FileText className="h-3 w-3" />
                              <span>{t('search.chpl')}</span>
                            </button>
                          )}
                          {drug.hasUlotkaPdf && onViewUlotka && (
                            <button
                              type="button"
                              onClick={() =>
                                onViewUlotka({
                                  drugId: drug.id,
                                  chplId: drug.chplId,
                                  drugName: drug.productName,
                                  activeSubstance: drug.commonName,
                                  strength: drug.strength,
                                  form: drug.form,
                                  texts: [],
                                })
                              }
                              className="inline-flex items-center gap-1 rounded bg-zinc-200/70 px-2 py-0.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-300 hover:text-zinc-800 dark:bg-zinc-700/70 dark:text-zinc-400 dark:hover:bg-zinc-600 dark:hover:text-zinc-300"
                            >
                              <FileText className="h-3 w-3" />
                              <span>{t('search.leaflet')}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // Normal drug card
              return (
                <div
                  key={drug.id}
                  className="relative rounded-lg border-2 border-zinc-900 bg-zinc-50 p-3 shadow-[2px_2px_0_0_#18181b] dark:border-zinc-100 dark:bg-zinc-800 dark:shadow-[2px_2px_0_0_#f4f4f5]"
                >
                  {/* X button — absolute top-right so it doesn't steal width from drug name */}
                  <button
                    type="button"
                    onClick={() => handleRemove(drug.id)}
                    className="absolute top-3 right-3 rounded border-2 border-zinc-900 bg-white p-0.5 text-zinc-600 shadow-[2px_2px_0_0_#18181b] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-red-100 hover:text-red-600 hover:shadow-[1px_1px_0_0_#18181b] dark:border-zinc-100 dark:bg-zinc-800 dark:text-zinc-400 dark:shadow-[2px_2px_0_0_#f4f4f5] dark:hover:bg-red-900/50 dark:hover:text-red-400 dark:hover:shadow-[1px_1px_0_0_#f4f4f5]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <div className="flex @[640px]:gap-3">
                    <Pill className="mt-1 hidden h-4 w-4 shrink-0 text-zinc-700 @[640px]:block dark:text-zinc-300" />
                    {/* Main content */}
                    <div className="min-w-0 flex-1">
                      <div className="min-w-0 pr-8">
                        <span className="font-medium text-zinc-900 dark:text-white">
                          {drug.productName}
                        </span>
                        {drug.commonName && (
                          <>
                            {' '}
                            <span className="text-sm text-zinc-500 @[640px]:text-base @[640px]:text-zinc-600 dark:text-zinc-400">
                              ({drug.commonName})
                            </span>
                          </>
                        )}
                      </div>
                      {/* Second line: strength/form + ChPL/Ulotka buttons */}
                      <div className="mt-1.5 flex items-end justify-between gap-2 @[640px]:mt-2">
                        {((drug.strength && drug.strength !== '-') ||
                          drug.form) && (
                          <div className="space-y-0.5 text-xs @[640px]:flex @[640px]:flex-wrap @[640px]:items-baseline @[640px]:space-y-0 @[640px]:gap-x-4 @[640px]:gap-y-1 @[640px]:text-sm">
                            {drug.strength && drug.strength !== '-' && (
                              <div className="flex items-baseline gap-1.5">
                                <span className="hidden text-xs font-medium tracking-wide text-zinc-400 uppercase @[640px]:inline dark:text-zinc-500">
                                  {t('search.strength')}
                                </span>
                                <span className="text-zinc-700 dark:text-zinc-300">
                                  {drug.strength}
                                </span>
                              </div>
                            )}
                            {drug.form && (
                              <div className="flex items-baseline gap-1.5">
                                <span className="hidden text-xs font-medium tracking-wide text-zinc-400 uppercase @[640px]:inline dark:text-zinc-500">
                                  {t('search.form')}
                                </span>
                                <span className="text-zinc-700 dark:text-zinc-300">
                                  {formatDrugForm(language, drug.form)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex shrink-0 flex-row items-center gap-1.5">
                          {drug.hasChplPdf && onViewChpl && (
                            <button
                              type="button"
                              onClick={() =>
                                onViewChpl(
                                  {
                                    drugId: drug.id,
                                    drugName: drug.productName,
                                    activeSubstance: drug.commonName,
                                    strength: drug.strength,
                                    form: drug.form,
                                    texts: [],
                                  },
                                  {
                                    hasUlotka: drug.hasUlotkaPdf,
                                  },
                                )
                              }
                              className="inline-flex items-center gap-1 rounded bg-zinc-200/70 px-2 py-0.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-300 hover:text-zinc-800 dark:bg-zinc-700/70 dark:text-zinc-400 dark:hover:bg-zinc-600 dark:hover:text-zinc-300"
                            >
                              <FileText className="hidden h-3 w-3 @[640px]:block" />
                              <span>{t('search.chpl')}</span>
                            </button>
                          )}
                          {drug.hasUlotkaPdf && onViewUlotka && (
                            <button
                              type="button"
                              onClick={() =>
                                onViewUlotka({
                                  drugId: drug.id,
                                  chplId: drug.chplId,
                                  drugName: drug.productName,
                                  activeSubstance: drug.commonName,
                                  strength: drug.strength,
                                  form: drug.form,
                                  texts: [],
                                })
                              }
                              className="inline-flex items-center gap-1 rounded bg-zinc-200/70 px-2 py-0.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-300 hover:text-zinc-800 dark:bg-zinc-700/70 dark:text-zinc-400 dark:hover:bg-zinc-600 dark:hover:text-zinc-300"
                            >
                              <FileText className="hidden h-3 w-3 @[640px]:block" />
                              <span>{t('search.leaflet')}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Input - Neubrutalism style, rendered BELOW selected drugs */}
      <div className="relative">
        {hasDrugs && (
          <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
            {selectedDrugs.length === 1 && (
              <span
                aria-hidden="true"
                className="mr-1 inline-block h-2 w-2 -translate-y-px rounded-full bg-green-500 align-middle"
              ></span>
            )}
            <span className="font-bold text-zinc-800 dark:text-zinc-200">
              {t('search.addAnotherDrug')}
            </span>
          </p>
        )}

        <div ref={searchContainerRef} className="relative">
          {isLoading ? (
            <Loader2 className="absolute top-1/2 left-4 hidden h-6 w-6 -translate-y-1/2 animate-spin text-zinc-900 @[640px]:block dark:text-zinc-100" />
          ) : (
            <Search
              className={cn(
                'absolute top-1/2 left-4 hidden h-5 w-5 -translate-y-1/2 @[640px]:block',
                'text-zinc-400',
              )}
            />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query.trim()) {
                setIsOpen(true);
                setHighlightedIndex(0);
              }
            }}
            onBlur={() => {
              // No-op: dropdown closing is handled by click-outside
            }}
            placeholder={
              isLoading
                ? t('search.loadingList')
                : isMaxDrugsReached
                  ? t('search.maxDrugsPlaceholder', { max: maxDrugs })
                  : (placeholder ?? t('search.placeholder'))
            }
            enterKeyHint="done"
            disabled={isLoading || isMaxDrugsReached}
            className={cn(
              'flex h-12 w-full rounded-lg border-2 py-3 pr-4 pl-4 text-base @[640px]:pr-11 @[640px]:pl-11',
              'bg-white text-zinc-900 placeholder:text-zinc-400',
              'transition-shadow focus:translate-x-[2px] focus:translate-y-[2px]',
              'focus:outline-none',
              'disabled:cursor-not-allowed',
              isLoading && 'animate-pulse bg-zinc-100 dark:bg-zinc-800',
              !isLoading && 'disabled:opacity-50',
              'border-zinc-900 shadow-[3px_3px_0_0_#18181b]',
              'focus:shadow-[1px_1px_0_0_#18181b]',
              'dark:border-zinc-100 dark:shadow-[3px_3px_0_0_#f4f4f5]',
              'dark:focus:shadow-[1px_1px_0_0_#f4f4f5]',
              'dark:bg-zinc-900 dark:text-white dark:placeholder:text-zinc-500',
              inputClassName,
            )}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setIsOpen(false);
                setHighlightedIndex(-1);
                inputRef.current?.focus({ preventScroll: true });
              }}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {inputEndAddon &&
            !query &&
            !isMaxDrugsReached &&
            selectedDrugs.length === 0 && (
              <div className="absolute inset-y-0 right-5 hidden translate-y-0.5 items-center sm:flex">
                {inputEndAddon}
              </div>
            )}
          {/* Search Results Dropdown */}
          {isOpen && visibleResults.length > 0 && (
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="absolute top-full z-50 mt-5 max-h-[70dvh] w-full overflow-auto rounded-lg border-2 border-zinc-900 bg-white shadow-[3px_3px_0_0_#18181b] @[640px]:max-h-80 dark:border-zinc-100 dark:bg-zinc-900 dark:shadow-[3px_3px_0_0_#f4f4f5]"
            >
              {visibleResults.map((result, index) => (
                <button
                  key={result.item.id}
                  type="button"
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(result.item)}
                  onMouseEnter={() => {
                    setHighlightedIndex(index);
                  }}
                  className={cn(
                    'group flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-sm',
                    'border-l-4 border-l-transparent',
                    'transition-colors hover:border-l-zinc-400 hover:bg-zinc-300 dark:hover:border-l-zinc-500 dark:hover:bg-zinc-600',
                    'focus:border-l-zinc-400 focus:bg-zinc-300 focus:outline-none dark:focus:border-l-zinc-500 dark:focus:bg-zinc-600',
                    index === highlightedIndex &&
                      'border-l-zinc-900 bg-zinc-200 dark:border-l-zinc-100 dark:bg-zinc-700',
                  )}
                >
                  {result.item.hasChplExtraction === false ? (
                    <Construction className="mt-0.5 hidden h-4 w-4 shrink-0 text-amber-500 @[640px]:block dark:text-amber-400" />
                  ) : (
                    <Pill className="mt-0.5 hidden h-4 w-4 shrink-0 text-zinc-500 @[640px]:block dark:text-zinc-400" />
                  )}
                  <div className="flex-1 overflow-hidden">
                    {/* Main line: productName (commonName) */}
                    <div className="truncate">
                      <span
                        className={cn(
                          'font-medium',
                          result.item.hasChplExtraction === false
                            ? 'text-zinc-500 dark:text-zinc-400'
                            : 'text-zinc-900 dark:text-white',
                        )}
                        dangerouslySetInnerHTML={{
                          __html: highlightMatch(
                            result.item.productName,
                            query,
                          ),
                        }}
                      />
                      {result.item.commonName && (
                        <span
                          className={cn(
                            'ml-1.5',
                            result.item.hasChplExtraction === false
                              ? 'text-zinc-400 dark:text-zinc-500'
                              : 'text-zinc-500 dark:text-zinc-400',
                          )}
                        >
                          (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightMatch(
                                result.item.commonName,
                                query,
                              ),
                            }}
                          />
                          )
                        </span>
                      )}
                    </div>
                    {/* Second line: strength and form — stacked on mobile, inline with · on desktop */}
                    {(result.item.strength && result.item.strength !== '-') ||
                    result.item.form ? (
                      <>
                        {/* Mobile: stacked */}
                        <div
                          className={cn(
                            'mt-0.5 text-xs @[640px]:hidden',
                            result.item.hasChplExtraction === false
                              ? 'text-zinc-400 dark:text-zinc-500'
                              : 'text-zinc-500 dark:text-zinc-400',
                          )}
                        >
                          {result.item.strength &&
                            result.item.strength !== '-' && (
                              <div
                                className="truncate"
                                title={result.item.strength}
                                dangerouslySetInnerHTML={{
                                  __html: highlightMatch(
                                    result.item.strength,
                                    query,
                                  ),
                                }}
                              />
                            )}
                          {result.item.form && (
                            <div
                              className="truncate"
                              title={result.item.form}
                              dangerouslySetInnerHTML={{
                                __html: highlightMatch(result.item.form, query),
                              }}
                            />
                          )}
                        </div>
                        {/* Desktop: inline with · separator */}
                        <div
                          className={cn(
                            'mt-0.5 hidden items-baseline text-xs @[640px]:flex',
                            result.item.hasChplExtraction === false
                              ? 'text-zinc-400 dark:text-zinc-500'
                              : 'text-zinc-500 dark:text-zinc-400',
                          )}
                        >
                          {result.item.strength &&
                            result.item.strength !== '-' && (
                              <span
                                className="max-w-[50%] truncate"
                                title={result.item.strength}
                                dangerouslySetInnerHTML={{
                                  __html: highlightMatch(
                                    result.item.strength,
                                    query,
                                  ),
                                }}
                              />
                            )}
                          {result.item.strength &&
                            result.item.strength !== '-' &&
                            result.item.form && (
                              <span className="mx-1.5 shrink-0">·</span>
                            )}
                          {result.item.form && (
                            <span
                              className="max-w-[50%] truncate"
                              title={result.item.form}
                              dangerouslySetInnerHTML={{
                                __html: highlightMatch(result.item.form, query),
                              }}
                            />
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                  {/* Selection indicator - visible on hover and highlighted row */}
                  <Plus
                    className={cn(
                      'h-4 w-4 shrink-0 text-zinc-600 opacity-100 transition-opacity duration-200 @[640px]:opacity-0 @[640px]:group-hover:opacity-100 dark:text-zinc-300',
                      index === highlightedIndex && 'opacity-100',
                    )}
                  />
                </button>
              ))}
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center gap-2 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                  <span className="text-sm text-zinc-500">
                    {t('search.loadingMore')}
                  </span>
                </div>
              )}
              {/* Show count if more results available */}
              {!isLoadingMore && visibleCount < allSearchResults.length && (
                <div className="border-t-2 border-zinc-900 bg-zinc-50 py-2 text-center text-xs text-zinc-600 dark:border-zinc-100 dark:bg-zinc-800 dark:text-zinc-400">
                  {t('search.shownOf', {
                    shown: visibleCount,
                    total: allSearchResults.length,
                  })}
                </div>
              )}
            </div>
          )}

          {/* No results message */}
          {isOpen &&
            (query.trim().length >= 2 || /^\d+$/.test(query.trim())) &&
            allSearchResults.length === 0 &&
            !isLoading && (
              <div className="absolute top-full z-50 mt-5 w-full rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-center shadow-[2px_2px_0_0_#fbbf24] dark:border-amber-700 dark:bg-amber-950/40 dark:shadow-[2px_2px_0_0_#a16207]">
                <div className="flex items-center justify-center gap-2">
                  <SearchX className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    {t('search.noResults', { query })}
                  </p>
                </div>
              </div>
            )}
        </div>

        {/* Max drugs reached message */}
        {isMaxDrugsReached && (
          <div className="mt-4 flex items-center gap-2.5 rounded-lg border-2 border-amber-500 bg-amber-100 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-400 dark:bg-amber-900/40 dark:text-amber-300">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span>{t('search.limitReached', { max: maxDrugs })}</span>
              {maxDrugs < TOP_PLAN_MAX_DRUGS && (
                <span className="text-xs font-normal">
                  {t('search.limitUpgradeQuestion')}{' '}
                  <Link
                    href={
                      upsellToPricing
                        ? '/pricing'
                        : language === 'en'
                          ? '/en/contact'
                          : '/contact'
                    }
                    className="font-semibold whitespace-nowrap underline underline-offset-2"
                  >
                    {t(
                      upsellToPricing
                        ? 'search.limitUpgradeLink'
                        : 'search.limitContactLink',
                    )}
                  </Link>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Search hint / examples - directly below search input */}
        {footer && !isMaxDrugsReached && selectedDrugs.length === 0 && (
          <div className="mt-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

// Fallback maximum when no tier-specific limit is provided via props.
// Matches the global ceiling (custom plans max).
const MAX_DRUGS_FALLBACK = 20;
// Highest paid tier's limit — mirrors `TIERS` in apps/web/config/subscription-tiers.config.ts
const TOP_PLAN_MAX_DRUGS = 15;

// Helper function to escape HTML special characters
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Highlight fuzzy match with matching chars in color, extra chars in grey
function highlightFuzzyMatch(
  matchedText: string,
  searchTerm: string,
  colorClass: string,
): string {
  const alignment = getCharacterAlignment(
    matchedText.toLowerCase(),
    searchTerm.toLowerCase(),
  );

  let result = '';
  let currentGroup: { chars: string; matched: boolean } | null = null;

  alignment.forEach(({ matched, originalIndex }) => {
    const originalChar = matchedText[originalIndex] ?? '';

    if (currentGroup === null) {
      currentGroup = { chars: originalChar, matched };
    } else if (currentGroup.matched === matched) {
      currentGroup.chars += originalChar;
    } else {
      // Flush current group
      result += renderGroup(currentGroup, colorClass);
      currentGroup = { chars: originalChar, matched };
    }
  });

  // Flush last group
  if (currentGroup) {
    result += renderGroup(currentGroup, colorClass);
  }

  return result;
}

function renderGroup(
  group: { chars: string; matched: boolean },
  colorClass: string,
): string {
  const escaped = escapeHtml(group.chars);
  if (group.matched) {
    return `<mark class="${colorClass}">${escaped}</mark>`;
  } else {
    // Extra characters - just underline, no background
    return `<span class="${MISSING_CHAR_STYLE}">${escaped}</span>`;
  }
}

// Get character-by-character alignment between two strings
// Returns which characters matched vs were inserted/deleted
function getCharacterAlignment(
  text: string,
  pattern: string,
): Array<{ char: string; matched: boolean; originalIndex: number }> {
  const m = text.length;
  const n = pattern.length;

  // Build DP matrix for edit distance
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (text[i - 1] === pattern[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]!;
      } else {
        dp[i]![j] =
          1 + Math.min(dp[i - 1]![j - 1]!, dp[i - 1]![j]!, dp[i]![j - 1]!);
      }
    }
  }

  // Backtrace to find alignment
  const result: Array<{
    char: string;
    matched: boolean;
    originalIndex: number;
  }> = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && text[i - 1] === pattern[j - 1]) {
      // Match
      result.unshift({
        char: text[i - 1]!,
        matched: true,
        originalIndex: i - 1,
      });
      i--;
      j--;
    } else if (i > 0 && j > 0 && dp[i]![j] === dp[i - 1]![j - 1]! + 1) {
      // Substitution - character in text doesn't match pattern
      result.unshift({
        char: text[i - 1]!,
        matched: false,
        originalIndex: i - 1,
      });
      i--;
      j--;
    } else if (i > 0 && dp[i]![j] === dp[i - 1]![j]! + 1) {
      // Deletion - extra character in text (not in pattern)
      result.unshift({
        char: text[i - 1]!,
        matched: false,
        originalIndex: i - 1,
      });
      i--;
    } else if (j > 0) {
      // Insertion - character in pattern not in text (skip in pattern)
      j--;
    }
  }

  return result;
}

// Find the best matching word in text using Levenshtein distance
function findBestWordMatch(
  text: string,
  term: string,
): { position: number; distance: number; matchedWord: string } | null {
  // Split text into words
  const words = text.split(/[\s\-()]+/).filter((w) => w.length >= 2);
  let bestMatch: {
    position: number;
    distance: number;
    matchedWord: string;
    score: number;
  } | null = null;

  for (const word of words) {
    const wordStart = text.indexOf(word);
    if (wordStart === -1) continue;

    // Skip words that are much shorter than the search term
    // "clarne" shouldn't match "ABE"
    if (word.length < term.length * 0.5) continue;

    // Compare the term against the word (or word prefix for long words)
    const compareLength = Math.min(word.length, term.length + 3);
    const wordPart = word.slice(0, compareLength).toLowerCase();
    const distance = levenshteinDistance(wordPart, term);

    // Calculate relative distance (lower = better)
    const relativeDistance = distance / Math.max(wordPart.length, term.length);

    // Calculate match score (higher = better)
    let score = 100 - relativeDistance * 100;

    // Bonus for prefix match (first 2+ chars match)
    if (wordPart.startsWith(term.slice(0, 2))) {
      score += 30;
    }
    if (term.length >= 3 && wordPart.startsWith(term.slice(0, 3))) {
      score += 20;
    }

    // Bonus for similar length
    const lengthRatio =
      Math.min(wordPart.length, term.length) /
      Math.max(wordPart.length, term.length);
    score += lengthRatio * 20;

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { position: wordStart, distance, matchedWord: word, score };
    }
  }

  return bestMatch;
}

// Find fuzzy match with details for highlighting
function findFuzzyMatchWithDetails(
  text: string,
  term: string,
): { start: number; end: number } | null {
  const match = findBestWordMatch(text, term);
  if (!match) return null;

  // Same acceptance logic as search
  const wordPart = match.matchedWord.slice(0, term.length + 3).toLowerCase();
  const relativeDistance =
    match.distance / Math.max(wordPart.length, term.length);
  const hasGoodPrefix = wordPart.startsWith(term.slice(0, 2));

  const isAcceptable =
    (hasGoodPrefix && relativeDistance <= 0.5) || relativeDistance <= 0.35;

  if (!isAcceptable) return null;

  // Return the full matched word for proper character alignment
  // The alignment algorithm will correctly identify which chars match vs are extra
  return {
    start: match.position,
    end: match.position + match.matchedWord.length,
  };
}

// Simple Levenshtein distance for fuzzy matching
function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0]![j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i]![j] = matrix[i - 1]![j - 1]!;
      } else {
        matrix[i]![j] = Math.min(
          matrix[i - 1]![j - 1]! + 1, // substitution
          matrix[i]![j - 1]! + 1, // insertion
          matrix[i - 1]![j]! + 1, // deletion
        );
      }
    }
  }

  return matrix[b.length]![a.length]!;
}
